<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useBaseView, useInvoices } from "@/composables";
import PageHeader from "@/components/header/PageHeader.vue";
import InvoicesFilters from "@/views/billing/invoices/components/InvoicesFilters.vue";
import InvoicesTable from "@/views/billing/invoices/components/InvoicesTable.vue";

import type { ListResponseDto } from "@/api/dto/list-response.dto";
import type { Invoice } from "@/api/billing/dto/invoice";
import type { InvoicesFilter } from "@/types/billing";

defineOptions({ name: "InvoicesPage" });

const { t } = useI18n();
const { setTitle, params } = useBaseView();
const { isLoading, fetchInvoices } = useInvoices();

const header = computed<string>(() => `${t("billing.billing")} / ${t("billing.invoices")}`);

function createDefaultFilter(): InvoicesFilter {
  return {
    status: null,
    number: "",
    orderId: "",
    refNumber: "",
    customerId: null,
    paymentType: null,
  };
}

const filter = ref<InvoicesFilter>(createDefaultFilter());
const data = ref<ListResponseDto<Invoice>>({ total: 0, page: 1, items: [] });

async function refresh() {
  const result = await fetchInvoices({
    status: filter.value.status,
    number: filter.value.number || null,
    orderId: filter.value.orderId || null,
    refNumber: filter.value.refNumber || null,
    customerId: filter.value.customerId,
    paymentType: filter.value.paymentType,
    page: params.value.page,
    pageSize: params.value.pageSize,
  });

  if (result) {
    data.value = result;
  }
}

async function search() {
  params.value.page = 1;
  await refresh();
}

async function clear() {
  filter.value = createDefaultFilter();
  await search();
}

async function updatePage(page: number) {
  params.value.page = page;
  await refresh();
}

async function updatePageSize(size: number) {
  params.value.page = 1;
  params.value.pageSize = size;
  await refresh();
}

onMounted(async () => {
  setTitle(t("billing.invoices"));
  await refresh();
});
</script>

<template>
  <v-container class="px-6 pt-8 pb-10 height-100" fluid>
    <page-header :header="header" />

    <invoices-filters v-model="filter" @clear="clear" @search="search" />

    <invoices-table
      :items="data.items"
      :loading="isLoading"
      :page="params.page"
      :page-size="params.pageSize"
      :total="data.total"
      @update:page="updatePage"
      @update:page-size="updatePageSize"
    />
  </v-container>
</template>
